function roundToNiceNumber(value: number, roundUp: boolean): number {
    if (value === 0) return 0;
    if (!Number.isFinite(value)) return roundUp ? 100 : 0;

    const absValue = Math.abs(value);
    const sign = value < 0 ? -1 : 1;
    const shouldRoundUp = sign < 0 ? !roundUp : roundUp;

    const magnitude = Math.floor(Math.log10(absValue));
    const power = 10 ** magnitude;
    const normalized = absValue / power;

    let niceNormalized: number;
    if (shouldRoundUp) {
        if (normalized <= 1) niceNormalized = 1;
        else if (normalized <= 1.5) niceNormalized = 1.5;
        else if (normalized <= 2) niceNormalized = 2;
        else if (normalized <= 2.5) niceNormalized = 2.5;
        else if (normalized <= 3) niceNormalized = 3;
        else if (normalized <= 4) niceNormalized = 4;
        else if (normalized <= 5) niceNormalized = 5;
        else if (normalized <= 6) niceNormalized = 6;
        else if (normalized <= 8) niceNormalized = 8;
        else niceNormalized = 10;
    } else {
        if (normalized < 1.5) niceNormalized = 1;
        else if (normalized < 2) niceNormalized = 1.5;
        else if (normalized < 2.5) niceNormalized = 2;
        else if (normalized < 3) niceNormalized = 2.5;
        else if (normalized < 4) niceNormalized = 3;
        else if (normalized < 5) niceNormalized = 4;
        else if (normalized < 6) niceNormalized = 5;
        else if (normalized < 8) niceNormalized = 6;
        else niceNormalized = 8;
    }

    const result = sign * niceNormalized * power;

    if (absValue < 1 && absValue > 0) {
        if (shouldRoundUp) {
            if (absValue <= 0.1) return sign * 0.1;
            if (absValue <= 0.15) return sign * 0.15;
            if (absValue <= 0.2) return sign * 0.2;
            if (absValue <= 0.25) return sign * 0.25;
            if (absValue <= 0.3) return sign * 0.3;
            if (absValue <= 0.4) return sign * 0.4;
            if (absValue <= 0.5) return sign * 0.5;
            if (absValue <= 0.6) return sign * 0.6;
            if (absValue <= 0.8) return sign * 0.8;
            return sign * 1.0;
        }
        if (absValue < 0.15) return sign * 0.1;
        if (absValue < 0.2) return sign * 0.15;
        if (absValue < 0.25) return sign * 0.2;
        if (absValue < 0.3) return sign * 0.25;
        if (absValue < 0.4) return sign * 0.3;
        if (absValue < 0.5) return sign * 0.4;
        if (absValue < 0.6) return sign * 0.5;
        if (absValue < 0.8) return sign * 0.6;
        if (absValue < 1.0) return sign * 0.8;
        return sign * 1.0;
    }

    return result;
}

/**
 * Axis bounds for `values`: the data range with `paddingPercent` of it added at either end,
 * rounded out to a "nice" number (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 times a power of ten) so the
 * ticks fall evenly. `forceMinZero` starts the axis at 0; `minimumMaxValue` is the least the
 * maximum may be. A single value is spread around itself; no values give 0 to 100.
 */
export function calculateChartRange(
    values: ReadonlyArray<number | null | undefined>,
    paddingPercent = 15,
    forceMinZero = true,
    minimumMaxValue?: number
): { min: number; max: number } {
    if (values.length === 0) {
        return { min: 0, max: 100 };
    }

    const validValues = values.filter((v): v is number => v != null && !Number.isNaN(v));

    if (validValues.length === 0) {
        return { min: 0, max: 100 };
    }

    const dataMin = Math.min(...validValues);
    const dataMax = Math.max(...validValues);

    if (dataMin === dataMax) {
        const value = dataMin;
        const padding = Math.max(value * 0.2, 5);
        const calculatedMin = value - padding;

        const min = forceMinZero ? 0 : calculatedMin;
        let max = value + padding;

        if (minimumMaxValue !== undefined && max < minimumMaxValue) {
            max = minimumMaxValue;
        }

        return {
            min: roundToNiceNumber(min, false),
            max: roundToNiceNumber(max, true),
        };
    }

    const range = dataMax - dataMin;
    const padding = range * (paddingPercent / 100);
    const calculatedMin = dataMin - padding;
    let calculatedMax = dataMax + padding;

    const min = forceMinZero ? 0 : calculatedMin;

    if (minimumMaxValue !== undefined && calculatedMax < minimumMaxValue) {
        calculatedMax = minimumMaxValue;
    }

    return {
        min: roundToNiceNumber(min, false),
        max: roundToNiceNumber(calculatedMax, true),
    };
}
